<template>
    <div>

        <div v-if="loading" class="text-center">
            <i class="fa fa-circle-o-notch fa-spin"></i> Loading
        </div>
        <div v-else>
            <vue-headful :title="(incident.IncidentId ? 'Edit Incident: ' + incident.IncidentId : 'New Incident') + ' :: MeritEHS'"/>

            <h4 class="py-2 mb-4">
                <ol class="breadcrumb font-weight-bold m-0 d-block d-md-inline-block">
                    <li class="breadcrumb-item font-weight-light d-block d-md-inline-block">
                        <router-link :to="`/incidents`">
                            <i class="fa fa-stethoscope"></i> Incidents
                        </router-link>
                    </li>
                    <template v-if="IncidentId">
                        <li class="breadcrumb-item font-weight-light d-block d-md-inline-block mt-md-0 mt-2">
                            <router-link :to="`/incidents/view/${IncidentId}`">
                                <incident-name :incident="incident" />
                            </router-link>
                        </li>
                        <li class="breadcrumb-item active d-block d-md-inline-block mt-md-0 mt-2"><i class="fa fa-edit"></i> Edit</li>
                    </template>
                    <template v-else>
                        <li class="breadcrumb-item active d-block d-md-inline-block mt-md-0 mt-2"><i class="fa fa-plus"></i> New</li>
                    </template>
                    <li class="ml-3 d-none d-md-inline-block" v-if="!edit_lock">
                        <button type="button" class="btn btn-sm btn-success" v-on:click="saveIncident(this)" :disabled="saving">
                            <span v-if="IncidentId">Update</span>
                            <span v-else>Create</span>
                            Incident
                        </button>
                        <button class="btn btn-sm btn-light btn-toggle" v-on:click="toggleAllSections()" :title="(baseSections.all ? 'Hide' : 'Show') + ' All Sections'">
                            <i :class="'fa fa-caret-' + (baseSections.all ? 'up' : 'down')"></i>
                            <template v-if="baseSections.all">Hide</template>
                            <template v-else>Show</template>
                            All Sections
                        </button>
                    </li>
                </ol>
            </h4>
            <div v-if="edit_lock">
                <div class="alert alert-warning">
                    <strong>Warning:</strong> This incident was checked out for edit by <a :href="`mailto:${incident.editor.email}`">{{ incident.editor.FirstName }} {{ incident.editor.LastName }}</a> at {{ incident.editing_timestamp | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}

                    <p v-if="$store.state.user.is_admin">
                        As an account administrator, you can <router-link :to="`/incidents/edit/${incident.IncidentId}/override`">Override the Incident Edit Lock</router-link>
                    </p>
                    <p v-else>
                        Only the Editor above or a System admin can reset the edit lock.
                    </p>
                </div>
            </div>
            <div v-else>
                <div class="d-block d-md-none mt-3 mb-3">
                    <button type="button" class="btn btn-sm btn-success" v-on:click="saveIncident(this)" :disabled="saving">
                        <span v-if="IncidentId">Update</span>
                        <span v-else>Create</span>
                        Incident
                    </button>
                    <button class="btn btn-sm btn-light btn-toggle" v-on:click="toggleAllSections()" :title="(baseSections.all ? 'Hide' : 'Show') + ' All Sections'">
                        <i :class="'fa fa-caret-' + (baseSections.all ? 'up' : 'down')"></i>
                        <template v-if="baseSections.all">Hide</template>
                        <template v-else>Show</template>
                        All Sections
                    </button>
                </div>

                <div class="row mt-2">
                    <div class="col-xl-10 col-lg-12">
                        <div id="form-content" style="position:relative;">
                            <div class="card mb-3" id="jobsite-input">
                                <div class="card-header">
                                    <strong :class="{'text-danger': errors.JobsiteId || errors.other_location || errors.foreman_id || errors.leadman_id || errors.superintendent_id || errors.project_manager_id || errors.supervisor_id}"><i class="fa fa-building"></i> Jobsite</strong>
                                    <i class="fa fa-asterisk text-danger small" v-if="(no_jobsite_set != 1 && is_other_location != 1 && !incident.JobsiteId) || (no_jobsite_set != 1 && is_other_location == 1 && !incident.other_location) || (settings.inci_require_foreman == 1 && !incident.foreman_id) || (settings.inci_require_superintendent == 1 && !incident.superintendent_id) || (settings.inci_require_pm == 1 && !incident.project_manager_id) || (settings.inci_require_supervisor == 1 && !incident.supervisor_id)"></i>
                                    <button class="btn btn-sm btn-light btn-toggle" v-on:click="baseSections.jobsite = !baseSections.jobsite" :title="(baseSections.jobsite ? 'Hide' : 'Show') + ' Jobsite Section'">
                                        <i :class="'fa fa-caret-' + (baseSections.jobsite ? 'up' : 'down')"></i>
                                    </button>
                                </div>
                                <div :class="{'card-body': true, 'd-none': !baseSections.jobsite}">
                                    <div v-bind:class="{ 'form-group': true, 'has-error': errors.JobsiteId }" class="row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.JobsiteId }">
                                            Jobsite: <i class="fa fa-asterisk text-danger small" v-if="no_jobsite_set != 1 && is_other_location != 1 && !incident.JobsiteId"></i>
                                        </label>
                                        <div class="col-sm-8">

                                            <jobsite-select ref="inciJobsiteSelect" :selectedValues="JobsiteValues" @vselected="onJobSiteSelectUpdate" :hasError="errors.JobsiteId ? true : false"></jobsite-select>

                                            <span v-if="errors.JobsiteId" class="text-danger">
                                                <span v-for="error in errors.JobsiteId">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>

                                    <template v-if="incident.IncidentTypeId && incidentTypeSettings[incident.IncidentTypeId].inci_require_jobsite == 0">
                                        <div class="form-group row">
                                            <label class="col-lg-3 col-sm-4 col-form-label text-sm-right">No Jobsite:</label>
                                            <div class="col-sm-8">
                                                <b-form-checkbox v-model="no_jobsite_set" :value="1" :unchecked-value="0" plain></b-form-checkbox>
                                            </div>
                                        </div>
                                    </template>

                                    <template v-if="account.inci_enable_other_location == 1">
                                        <div class="form-group row">
                                            <label class="col-lg-3 col-sm-4 col-form-label text-sm-right">Is Other Location?</label>
                                            <div class="col-sm-8">
                                                <b-form-checkbox v-model="is_other_location" :value="1" :unchecked-value="0" plain></b-form-checkbox>
                                            </div>
                                        </div>

                                        <div class="form-group row" v-if="is_other_location == 1">
                                            <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.other_location }">
                                                Other Location:
                                                <i v-if="no_jobsite_set != 1 && !incident.other_location" class="fa fa-asterisk text-danger small"></i>
                                            </label>
                                            <div class="col-sm-8">

                                                <b-form-input v-model="incident.other_location" type="text" placeholder="Enter the Other Location" :class="{'is-invalid': errors.other_location}"></b-form-input>

                                                <span v-if="errors.other_location" class="text-danger">
                                                    <span v-for="error in errors.other_location">{{ error }}</span>
                                                </span>
                                            </div>
                                        </div>
                                    </template>

                                    <template v-if="this.$store.state.user.subcontractor_id == null">
                                        <div class="form-group row">
                                            <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.foreman_id }">
                                                Foreman:
                                                 <i class="fa fa-asterisk text-danger small" v-if="settings.inci_require_foreman == 1 && !incident.foreman_id"></i>
                                            </label>
                                            <div class="col-sm-8">

                                                <employee-select ref="foremanSelector" :selectedValues="ForemanValues" @vselected="onForemanSelect" :hasError="errors.foreman_id ? true : false"></employee-select>

                                                <span v-if="errors.foreman_id" class="text-danger">
                                                    <span v-for="error in errors.foreman_id">{{ error }}</span>
                                                </span>

                                                <template  v-if="this.$root.hasPermission('employees.manage')">
                                                    <div class="mt-1 mb-1">
                                                        <a href="javascript:void(0)" v-on:click="$refs.newForemanModal.show()"><small><i class="fa fa-plus"></i> Create a Foreman</small></a>
                                                    </div>
                                                    <div>
                                                        <employee-modal ref="newForemanModal" @employeeCreated="foremanCreated" :showButton="false" />
                                                    </div>
                                                </template>
                                            </div>
                                        </div>
                                        <div class="form-group row">
                                            <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.leadman_id }">Leadman:</label>
                                            <div class="col-sm-8">

                                                <employee-select ref="leadmanSelector" :selectedValues="LeadmanValues" @vselected="onLeadmanSelect" :hasError="errors.leadman_id ? true : false"></employee-select>

                                                <span v-if="errors.leadman_id" class="text-danger">
                                                    <span v-for="error in errors.leadman_id">{{ error }}</span>
                                                </span>

                                                <template  v-if="this.$root.hasPermission('employees.manage')">
                                                    <div class="mt-1 mb-1">
                                                        <a href="javascript:void(0)" v-on:click="$refs.newLeadmanModal.show()"><small><i class="fa fa-plus"></i> Create a Leadman</small></a>
                                                    </div>
                                                    <div>
                                                        <employee-modal ref="newLeadmanModal" @employeeCreated="leadmanCreated" :showButton="false" />
                                                    </div>
                                                </template>
                                            </div>
                                        </div>
                                    </template>

                                    <div v-bind:class="{ 'form-group': true, 'has-error': errors.superintendent_id }" class="row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.superintendent_id }">
                                            Superintendent:
                                            <i class="fa fa-asterisk text-danger small" v-if="settings.inci_require_superintendent == 1 && !incident.superintendent_id"></i>
                                        </label>
                                        <div class="col-sm-8">

                                            <employee-select ref="incSuperSelect" :selectedValues="SuperintendentValues" @vselected="onSuperSelect" :hasError="errors.superintendent_id ? true : false" />

                                            <span v-if="errors.superintendent_id" class="text-danger">
                                                <span v-for="error in errors.superintendent_id">{{ error }}</span>
                                            </span>

                                            <template  v-if="this.$root.hasPermission('employees.manage')">
                                                <div class="mt-1 mb-1">
                                                    <a href="javascript:void(0)" v-on:click="showNewSuperModal()"><small><i class="fa fa-plus"></i> Create a Superintendent</small></a>
                                                </div>

                                                <div>
                                                    <employee-modal ref="newSuperModal" @employeeCreated="superCreated" :showButton="false" />
                                                </div>
                                            </template>
                                        </div>
                                    </div>

                                    <div v-bind:class="{ 'form-group': true, 'has-error': errors.project_manager_id }" class="row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.project_manager_id }">
                                            Project Manager:
                                            <i class="fa fa-asterisk text-danger small" v-if="settings.inci_require_pm == 1 && !incident.project_manager_id"></i>
                                        </label>
                                        <div class="col-sm-8">

                                            <employee-select ref="incProjectManagerSelect" :selectedValues="ProjectManagerValues" @vselected="onProjectManagerSelect" :hasError="errors.project_manager_id ? true : false" />

                                            <span v-if="errors.project_manager_id" class="text-danger">
                                                <span v-for="error in errors.project_manager_id">{{ error }}</span>
                                            </span>

                                            <template  v-if="this.$root.hasPermission('employees.manage')">
                                                <div class="mt-1 mb-1">
                                                    <a href="javascript:void(0)" v-on:click="showNewProjectManagerModal()"><small><i class="fa fa-plus"></i> Create a Project Manager</small></a>
                                                </div>

                                                <div>
                                                    <employee-modal ref="newProjectManagerModal" @employeeCreated="projectManagerCreated" :showButton="false" />
                                                </div>
                                            </template>
                                        </div>
                                    </div>

                                    <template v-if="settings.inci_require_supervisor == 1">
                                        <div v-bind:class="{ 'form-group row': true, 'has-error': errors.supervisor_id }">
                                            <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.supervisor_id }">
                                                Supervisor:
                                                <i class="fa fa-asterisk text-danger small" v-if="settings.inci_require_supervisor == 1 && !incident.supervisor_id && is_other_supervisor !== 1"></i>
                                            </label>
                                            <div class="col-sm-8">

                                                <employee-select ref="incSupervisorSelect" :selectedValues="SupervisorValues" @vselected="onSupervisorSelect" :hasError="errors.supervisor_id ? true : false" />

                                                <span v-if="errors.supervisor_id" class="text-danger">
                                                    <span v-for="error in errors.supervisor_id">{{ error }}</span>
                                                </span>
                                            </div>
                                        </div>

                                        <div v-bind:class="{ 'form-group row': true }">
                                            <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true }">
                                                Other Supervisor:
                                            </label>
                                            <div class="col-sm-8">
                                                <b-form-checkbox v-model="is_other_supervisor" :value="1" :unchecked-value="0" plain></b-form-checkbox>
                                            </div>
                                        </div>

                                        <div v-bind:class="{ 'form-group row': true, 'has-error': errors.other_supervisor_name }" v-if="is_other_supervisor == 1">
                                            <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.other_supervisor_name }">
                                                Supervisor Name:
                                                <i class="fa fa-asterisk text-danger small" v-if="!incident.other_supervisor_name"></i>
                                            </label>
                                            <div class="col-sm-8">
                                                <b-form-input v-model="incident.other_supervisor_name" placeholder="Enter Other Supervisor Name" :class="{'is-invalid': errors.other_supervisor_name}" />

                                                <div v-if="errors.other_supervisor_name" class="text-danger mt-2">
                                                    <span v-for="error in errors.other_supervisor_name">{{ error }}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </template>

                                    <template v-if="incidentType.sections">
                                        <incident-sections-builder
                                            :sections="incidentTypeSections"
                                            :initialValues="custom_fields"
                                            :errors="errors"
                                            baseSection="jobsite"
                                            labelSize="col-lg-3 col-md-3 col-sm-4"
                                            fieldSize="col-sm-8"
                                            @customFieldsChanged="setIncidentCustomFields" />
                                    </template>
                                </div>
                            </div>

                            <div class="card mb-3" id="employee-input">
                                <div class="card-header">
                                    <strong :class="{'text-danger': errors.EmployeeId || errors.TempEmployeeName || errors.SubcontractorName || errors.ContractorCompany || errors.EmployeeTitle || errors.EmployeeAddress || errors.EmployeeCity || errors.EmployeeState || errors.EmployeeZip || errors.EmployeePhone || errors.additional_employees}"><i class="fa fa-user"></i> Employee</strong>
                                    <i v-if="(incident.IsContractor != 1 && incident.IsTempEmployee != 1 && !incident.EmployeeId) || (incident.IsTempEmployee == 1 && !incident.TempEmployeeName) || (incident.IsContractor == 1 && (!incident.ContractorCompany || !incident.SubcontractorName))" class="fa fa-asterisk text-danger small"></i>
                                    <button class="btn btn-sm btn-light btn-toggle" v-on:click="baseSections.employee = !baseSections.employee" :title="(baseSections.employee ? 'Hide' : 'Show') + ' Employee Section'">
                                        <i :class="'fa fa-caret-' + (baseSections.employee ? 'up' : 'down')"></i>
                                    </button>
                                </div>
                                <div :class="{'card-body': true, 'd-none': !baseSections.employee}">
                                    <template v-if="(this.$store.state.user.subcontractor_id && this.$root.hasPermission('employees.view')) || !this.$store.state.user.subcontractor_id">
                                        <div v-bind:class="{ 'form-group': true, 'has-error': errors.EmployeeId }" class="row">
                                            <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.EmployeeId }">Employee: <i v-if="incident.IsContractor != 1 && incident.IsTempEmployee != 1 && !incident.EmployeeId" class="fa fa-asterisk text-danger small"></i></label>
                                            <div class="col-sm-8">

                                                <employee-select ref="employeeSelector" :selectedValues="EmployeeValues" @vselected="onEmployeeSelect" :hasError="errors.EmployeeId ? true : false"></employee-select>

                                                <span v-if="errors.EmployeeId" class="text-danger">
                                                    <span v-for="(error,i) in errors.EmployeeId"><span v-if="i > 0">. </span>{{ error }}</span>
                                                </span>

                                                <template  v-if="this.$root.hasPermission('employees.manage')">
                                                    <div class="mt-1 mb-1">
                                                        <a href="javascript:void(0)" v-on:click="showNewEmployeeModal()"><small><i class="fa fa-plus"></i> Create a New Employee</small></a>
                                                    </div>

                                                    <div>
                                                        <employee-modal ref="newEmployeeModal" @employeeCreated="employeeCreated" :showButton="false" />
                                                    </div>
                                                </template>
                                            </div>
                                        </div>

                                        <div class="form-group row">
                                            <label class="col-lg-3 col-sm-4 col-form-label text-sm-right">Other Employee?</label>
                                            <div class="col-sm-8">
                                                <b-form-checkbox v-model="incident.IsTempEmployee" value="1" unchecked-value="0" plain>
                                                    <span class="small">Check if Employee is Temporary Labor, a Subcontractor Employee, or Other.</span>
                                                </b-form-checkbox>
                                            </div>
                                        </div>

                                        <div class="form-group row" v-if="incident.IsTempEmployee == 1">
                                            <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.TempEmployeeName }">
                                                Other Employee Name:<i v-if="!incident.TempEmployeeName" class="fa fa-asterisk text-danger small"></i>
                                            </label>
                                            <div class="col-sm-8">

                                                <b-form-input v-model="incident.TempEmployeeName" type="text" placeholder="Enter Temporary, Subcontractor, or Other Employee Name" :class="{'is-invalid': errors.TempEmployeeName}"></b-form-input>

                                                <span v-if="errors.TempEmployeeName" class="text-danger">
                                                    <span v-for="error in errors.TempEmployeeName">{{ error }}</span>
                                                </span>
                                            </div>
                                        </div>

                                    </template>

                                    <div class="form-group row">
                                        <label class="col-lg-3 col-sm-4 col-form-label text-sm-right">Is Subcontractor?:</label>
                                        <div class="col-sm-8">
                                            <b-form-checkbox v-model="incident.IsContractor" value="1" unchecked-value="0" plain></b-form-checkbox>
                                        </div>
                                    </div>

                                    <template v-if="incident.IsContractor == 1">
                                        <div class="form-group row">
                                            <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.subcontractor_id }">
                                                Subcontractor: <i v-if="!incident.subcontractor_id && incident.is_other_subcontractor != 1" class="fa fa-asterisk text-danger small"></i>
                                            </label>
                                            <div class="col-sm-8">
                                                <b-form-select v-model="incident.subcontractor_id"
                                                    :options="subcontractorOptions"
                                                    :state="errors.subcontractor_id ? false : null"
                                                    :disabled="incident.is_other_subcontractor == 1" />

                                                <template  v-if="this.$root.hasPermission('subcontractors.manage') && incident.is_other_subcontractor != 1">
                                                    <div class="mt-2">
                                                        <subcontractor-modal :showButton="true" @subcontractorCreated="subcontractorCreated" />
                                                    </div>
                                                </template>

                                                <span v-if="errors.subcontractor_id" class="text-danger">
                                                    <span v-for="error in errors.subcontractor_id">{{ error }}</span>
                                                </span>
                                            </div>
                                        </div>
                                        <div class="form-group row">
                                            <label class="col-lg-3 col-sm-4 col-form-label text-sm-right">Other Subcontractor?</label>
                                            <div class="col-sm-8">
                                                <b-form-checkbox v-model="incident.is_other_subcontractor" value="1" unchecked-value="0" plain>
                                                    <span class="small">Check if the Subcontractor is not in your list of known subs. This will enable a Free Text Subcontractor Name field.</span>
                                                </b-form-checkbox>
                                            </div>
                                        </div>
                                        <div class="form-group row" v-if="incident.is_other_subcontractor == 1">
                                            <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.ContractorCompany }">
                                                Other Subcontractor Name: <i v-if="!incident.ContractorCompany" class="fa fa-asterisk text-danger small"></i>
                                            </label>
                                            <div class="col-sm-8">
                                                <b-form-input v-model="incident.ContractorCompany" type="text" placeholder="Enter Subcontractor Company Name" :class="{'is-invalid': errors.ContractorCompany}"></b-form-input>

                                                <span v-if="errors.ContractorCompany" class="text-danger">
                                                    <span v-for="error in errors.ContractorCompany">{{ error }}</span>
                                                </span>
                                            </div>
                                        </div>
                                    </template>

                                    <hr/>

                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.EmployeeTitle }">Employee Position/Title:</label>
                                        <div class="col-sm-8">

                                            <b-form-input v-model="incident.EmployeeTitle" type="text" placeholder="Enter Employee Position or Title" :state="errors.EmployeeTitle ? false : null"></b-form-input>

                                            <span v-if="errors.EmployeeTitle" class="text-danger">
                                                <span v-for="error in errors.EmployeeTitle">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div v-bind:class="{ 'form-group': true, 'has-error': errors.EmployeeAddress }" class="row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.EmployeeAddress }">Employee Address:</label>
                                        <div class="col-sm-8">

                                            <b-form-input v-model="incident.EmployeeAddress" type="text" placeholder="Enter Employee Address" :class="{'is-invalid': errors.EmployeeAddress}"></b-form-input>

                                            <span v-if="errors.EmployeeAddress" class="text-danger">
                                                <span v-for="error in errors.EmployeeAddress">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div v-bind:class="{ 'form-group': true, 'has-error': errors.EmployeeCity }" class="row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.EmployeeCity }">Employee City:</label>
                                        <div class="col-sm-8">

                                            <b-form-input v-model="incident.EmployeeCity" type="text" placeholder="Enter Employee City" :class="{'is-invalid': errors.EmployeeCity}"></b-form-input>

                                            <span v-if="errors.EmployeeCity" class="text-danger">
                                                <span v-for="error in errors.EmployeeCity">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div v-bind:class="{ 'form-group': true, 'has-error': errors.EmployeeState }" class="row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.EmployeeState }">Employee State:</label>
                                        <div class="col-sm-8">

                                            <b-form-input v-model="incident.EmployeeState" type="text" placeholder="Enter Employee State" :class="{'is-invalid': errors.EmployeeState}"></b-form-input>

                                            <span v-if="errors.EmployeeState" class="text-danger">
                                                <span v-for="error in errors.EmployeeState">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div v-bind:class="{ 'form-group': true, 'has-error': errors.EmployeeZip }" class="row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.EmployeeZip }">Employee Zip:</label>
                                        <div class="col-sm-8">

                                            <b-form-input v-model="incident.EmployeeZip" type="text" placeholder="Enter Employee Zip Code" :class="{'is-invalid': errors.EmployeeZip}"></b-form-input>

                                            <span v-if="errors.EmployeeZip" class="text-danger">
                                                <span v-for="error in errors.EmployeeZip">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div v-bind:class="{ 'form-group': true, 'has-error': errors.EmployeePhone }" class="row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.EmployeePhone }">Employee Phone:</label>
                                        <div class="col-sm-8">

                                            <b-form-input v-model="incident.EmployeePhone" type="text" placeholder="Enter Employee Phone Number" :class="{'is-invalid': errors.EmployeePhone}"></b-form-input>

                                            <span v-if="errors.EmployeePhone" class="text-danger">
                                                <span v-for="error in errors.EmployeePhone">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <hr/>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.additional_employees }">
                                            Additional Employee(s):
                                        </label>
                                        <div class="col-sm-8">
                                            <employee-select ref="addEmployeeSelector" @vselected="addEmployeeSelected"></employee-select>

                                            <template  v-if="this.$root.hasPermission('employees.manage')">
                                                <div class="mt-1 mb-1">
                                                    <a href="javascript:void(0)" v-on:click="$refs.newAdditionalEmployeeModal.show()"><small><i class="fa fa-plus"></i> Create a New Employee</small></a>
                                                </div>

                                                <div>
                                                    <employee-modal ref="newAdditionalEmployeeModal" @employeeCreated="addEmployeeSelected" :showButton="false" />
                                                </div>
                                            </template>
                                            <hr/>

                                            <ul class="list-unstyled">
                                                <li v-for="emp in incident.additional_employees">
                                                    <button type="button" class="btn btn-sm btn-link" v-on:click="removeAddEmployee(emp.EmployeeId)"><i class="fa fa-remove text-danger"></i></button>
                                                    {{ emp.FirstName + ' ' + emp.LastName }}
                                                    <template v-if="emp.EmployeeNumber">
                                                        - {{ emp.EmployeeNumber }}
                                                    </template>
                                                </li>
                                            </ul>

                                        </div>
                                    </div>

                                    <template v-if="incidentType.sections">
                                        <incident-sections-builder
                                            :sections="incidentTypeSections"
                                            :initialValues="custom_fields"
                                            :errors="errors"
                                            baseSection="employee"
                                            labelSize="col-lg-3 col-md-3 col-sm-4"
                                            fieldSize="col-sm-8"
                                            @customFieldsChanged="setIncidentCustomFields" ></incident-sections-builder>
                                    </template>
                                </div>
                            </div>

                            <div class="card mb-3" id="disc-actions" v-if="settings.inci_require_disc_actions">
                                <div :class="{ 'card-header': true, 'text-danger': (errors.disc_action_required || errors.disc_action_id) }">
                                    <i class="fa fa-gavel"></i> <strong>Employee Disciplinary Action</strong>

                                    <button class="btn btn-sm btn-light btn-toggle" v-on:click="baseSections.disc_actions = !baseSections.disc_actions" :title="(baseSections.disc_actions ? 'Hide' : 'Show') + ' Disciplinary Actions Section'">
                                        <i :class="'fa fa-caret-' + (baseSections.disc_actions ? 'up' : 'down')"></i>
                                    </button>
                                </div>
                                <div :class="{'card-body': true, 'd-none': !baseSections.disc_actions}">
                                    <div class="form-group row mt-3">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.disc_action_required }">
                                            Does this Incident Require Employee Disciplinary Action? <i class="fa fa-asterisk text-danger small"></i>
                                        </label>
                                        <div class="col-sm-8">
                                            <b-form-radio-group v-model="incident.disc_action_required" :options="[{value: 0, text: 'No'}, {value: 1, text: 'Yes'}]" plain name="disc_action_required"></b-form-radio-group>
                                        </div>
                                    </div>

                                    <template v-if="incident.disc_action_required == 1 || incident.disc_action_id">
                                        <div class="form-group row mt-3">
                                            <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.disc_action_id }">
                                                Disciplinary Action? <i class="fa fa-asterisk text-danger small"></i>
                                            </label>
                                            <div class="col-sm-8">
                                                <template v-if="disc_action.disc_action_id">
                                                    <template v-if="disc_action.disc_violation_id">{{ disc_action.violation_name }}</template>
                                                    <template v-else>{{ disc_action.other_violation_name }}</template>
                                                     on {{ disc_action.disc_action_date | moment("MM/DD/YYYY") }} by {{ disc_action.employee_first_name }} {{ disc_action.employee_last_name }}
                                                </template>

                                                 <button class="btn btn-sm btn-link" v-on:click="showDiscAction()"><i class="fa fa-edit"></i> Edit</button>

                                                 <div v-if="errors.disc_action_id" class="text-danger">
                                                     <span v-for="error in errors.disc_action_id">{{ error }}</span>
                                                 </div>
                                            </div>
                                        </div>
                                    </template>

                                    <disciplinary-edit-modal
                                        ref="discEditModal"
                                        :incidentId="incident.IncidentId"
                                        :id="incident.disc_action_id"
                                        :fillable="{supervisor: SupervisorValues, employee: EmployeeValues, jobsite: JobsiteValues}"
                                        @actionUpdated="setIncidentDiscAction"></disciplinary-edit-modal>

                                    <template v-if="incidentType.sections">
                                        <incident-sections-builder
                                            :sections="incidentTypeSections"
                                            :initialValues="custom_fields"
                                            :errors="errors"
                                            baseSection="disc_action"
                                            labelSize="col-lg-3 col-md-3 col-sm-4"
                                            fieldSize="col-sm-8"
                                            @customFieldsChanged="setIncidentCustomFields" ></incident-sections-builder>
                                    </template>
                                </div>
                            </div>

                            <div class="card mb-3" id="equipment-input">
                                <div class="card-header">
                                    <strong><i class="fa fa-truck"></i> Equipment</strong>
                                    <button class="btn btn-sm btn-light btn-toggle" v-on:click="baseSections.equipment = !baseSections.equipment" :title="(baseSections.equipment ? 'Hide' : 'Show') + ' Equipment Section'">
                                        <i :class="'fa fa-caret-' + (baseSections.equipment ? 'up' : 'down')"></i>
                                    </button>
                                </div>
                                <div :class="{'card-body': true, 'd-none': !baseSections.equipment}">

                                    <div v-bind:class="{ 'form-group': true, 'has-error': errors.equipment_id }" class="row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.equipment_id }">Known Equipment:</label>
                                        <div class="col-sm-8">

                                            <equipment-select :selectedValues="EquipmentValues" @vuselected="onEquipmentSelect" :hasError="errors.equipment_id ? true : false" />

                                            <span v-if="errors.equipment_id" class="text-danger">
                                                <span v-for="error in errors.equipment_id">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>

                                    <div class="form-group row">
                                        <label class="col-lg-3 col-md-4 col-form-label text-sm-right text-muted">Or:</label>
                                        <div class="col-lg-8 col-md-8 text-primary">Using the "Known Equipment" field will attach the incident to an Equipment record. Use the fields below for free text equipment information.</div>
                                    </div>

                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-4 col-form-label text-sm-right': true, 'text-danger': errors.Equipment }">Equipment Name:</label>
                                        <div class="col-lg-8 col-md-8">

                                            <input type="text" v-model="incident.Equipment" :class="{ 'form-control': true, 'is-invalid': errors.Equipment }" placeholder="Enter Equipment" />

                                            <span v-if="errors.Equipment" class="text-danger">
                                                <span v-for="error in errors.Equipment">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-4 col-form-label text-sm-right': true, 'text-danger': errors.EquipmentMake }">Equipment Make:</label>
                                        <div class="col-lg-8 col-md-8">

                                            <input type="text" v-model="incident.EquipmentMake" :class="{ 'form-control': true, 'is-invalid': errors.EquipmentMake }" placeholder="Enter Equipment Make" />

                                            <span v-if="errors.EquipmentMake" class="text-danger">
                                                <span v-for="error in errors.EquipmentMake">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-4 col-form-label text-sm-right': true, 'text-danger': errors.EquipmentModel }">Equipment Model:</label>
                                        <div class="col-lg-8 col-md-8">

                                            <input type="text" v-model="incident.EquipmentModel" :class="{ 'form-control': true, 'is-invalid': errors.EquipmentModel }" placeholder="Enter Equipment Model" />

                                            <span v-if="errors.EquipmentModel" class="text-danger">
                                                <span v-for="error in errors.EquipmentModel">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-4 col-form-label text-sm-right': true, 'text-danger': errors.EquipmentSerial }">Equipment Serial#:</label>
                                        <div class="col-lg-8 col-md-8">

                                            <input type="text" v-model="incident.EquipmentSerial" :class="{ 'form-control': true, 'is-invalid': errors.EquipmentSerial }" placeholder="Enter Equipment Serial Number" />

                                            <span v-if="errors.EquipmentSerial" class="text-danger">
                                                <span v-for="error in errors.EquipmentSerial">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>

                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.additional_equipment }">Additional Equipment:</label>
                                        <div class="col-sm-8">

                                            <equipment-select ref="addEquipmentSelector" @vuselected="addEquipmentSelected"></equipment-select>
                                            <hr/>

                                            <ul class="list-unstyled">
                                                <li v-for="equip in incident.additional_equipment">
                                                    <button type="button" class="btn btn-sm btn-link" v-on:click="removeAddEquipment(equip.equipment_id)"><i class="fa fa-remove text-danger"></i></button>
                                                    {{ equip.equipment_type_name + ': ' + equip.serial_number }}
                                                    <template v-if="equip.model_number">
                                                        - {{ equip.model_number }}
                                                    </template>
                                                </li>
                                            </ul>

                                        </div>
                                    </div>

                                    <template v-if="incidentType.sections">
                                        <incident-sections-builder
                                            :sections="incidentTypeSections"
                                            :initialValues="custom_fields"
                                            :errors="errors"
                                            baseSection="equipment"
                                            labelSize="col-lg-3 col-md-3 col-sm-4"
                                            fieldSize="col-sm-8"
                                            @customFieldsChanged="setIncidentCustomFields" ></incident-sections-builder>
                                    </template>
                                </div>
                            </div>

                            <div class="card mb-3" id="incident-input">
                                <div class="card-header">
                                    <strong :class="{'text-danger': errors.IncidentTypeId || errors.IncidentDate || errors.IsIncidentTimeUndetermined || errors.IncidentTime || errors.IncidentLocation || errors.IncidentReportedDate || errors.BeginWorkTime || errors.IncidentWitness || errors.CrewSize || errors.due_to_rework}">
                                        <i class="fa fa-stethoscope"></i> Incident Details</strong>
                                    <i v-if="showIncidentHeaderAsterisk" class="fa fa-asterisk text-danger small"></i>
                                    <button class="btn btn-sm btn-light btn-toggle" v-on:click="baseSections.incident = !baseSections.incident" :title="(baseSections.incident ? 'Hide' : 'Show') + ' Incident Details Section'">
                                        <i :class="'fa fa-caret-' + (baseSections.incident ? 'up' : 'down')"></i>
                                    </button>
                                </div>
                                <div :class="{'card-body': true, 'd-none': !baseSections.incident}">
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.IncidentTypeId }">
                                            Type of Incident
                                            <i class="fa fa-question-circle" title="You may only change incident types between types using the same set of custom fields"></i>:
                                            <i v-if="!incident.IncidentTypeId" class="fa fa-asterisk text-danger small"></i>
                                        </label>
                                        <div class="col-sm-8">

                                            <b-form-select v-model="incident.IncidentTypeId"
                                                :options="incidentTypes"
                                                :state="errors.IncidentTypeId ? false : null" />

                                            <span v-if="errors.IncidentTypeId" class="text-danger">
                                                <span v-for="error in errors.IncidentTypeId">{{ error }}</span>
                                            </span>

                                            <i class="fa fa-question-circle" title="If you do not see the incident type you need in the dropdown above, there are most likely different custom fields associated with it. Use the migration tool to change the incident to any other type."></i>
                                            <router-link :to="`/incidents/change-type/${incident.IncidentId}`">Migrate to a different incident type <i class="fa fa-caret-right"></i></router-link>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.IncidentDate }">Date of Incident: <i v-if="!incident.IncidentDate" class="fa fa-asterisk text-danger small"></i></label>
                                        <div class="col-sm-8">
                                            <b-form-input v-model="incident.IncidentDate" type="date" :class="{ 'is-invalid': errors.IncidentDate }" placeholder="Enter the Date of the Incident" />

                                            <span v-if="errors.IncidentDate" class="text-danger">
                                                <span v-for="error in errors.IncidentDate">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.IsIncidentTimeUndetermined }">Is Incident Time Undetermined:</label>
                                        <div class="col-sm-8">

                                            <b-form-checkbox v-model="incident.IsIncidentTimeUndetermined" :value="1" :unchecked-value="0" plain :state="errors.IsIncidentTimeUndetermined ? false : null">YES</b-form-checkbox>

                                            <span v-if="errors.IsIncidentTimeUndetermined" class="text-danger">
                                                <span v-for="error in errors.IsIncidentTimeUndetermined">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.IncidentTime }">
                                            Time of Incident: <i v-if="!incident.IncidentTime" class="fa fa-asterisk text-danger small"></i>
                                        </label>
                                        <div class="col-sm-8">
                                            <b-form-input v-model="incident.IncidentTime" type="time"  :class="{ 'is-invalid': errors.IncidentTime }" placeholder="Enter Time of Incident" />

                                            <span v-if="errors.IncidentTime" class="text-danger">
                                                <span v-for="error in errors.IncidentTime">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.IncidentLocation }">Exact Location: <i v-if="!incident.IncidentLocation" class="fa fa-asterisk text-danger small"></i></label>
                                        <div class="col-sm-8">

                                            <input type="text" v-model="incident.IncidentLocation" :class="{ 'form-control': true, 'is-invalid': errors.IncidentLocation }" placeholder="Enter Exact Location of the Incident" />

                                            <span v-if="errors.IncidentLocation" class="text-danger">
                                                <span v-for="error in errors.IncidentLocation">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.IncidentReportedDate }">
                                            Date Employee Reported Incident: <i v-if="!incident.IncidentReportedDate" class="fa fa-asterisk text-danger small"></i>
                                        </label>
                                        <div class="col-sm-8">
                                            <b-form-input v-model="incident.IncidentReportedDate" type="date" :class="{ 'is-invalid': errors.IncidentReportedDate }" placeholder="Enter the Date the Incident was Reported" />

                                            <span v-if="errors.IncidentReportedDate" class="text-danger">
                                                <span v-for="error in errors.IncidentReportedDate">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.BeginWorkTime }">
                                            Time Work Began: <i v-if="!incident.BeginWorkTime" class="fa fa-asterisk text-danger small"></i>
                                        </label>
                                        <div class="col-sm-8">
                                            <b-form-input v-model="incident.BeginWorkTime" type="time"  :class="{ 'is-invalid': errors.BeginWorkTime }" placeholder="Enter Time Work Began" />

                                            <span v-if="errors.BeginWorkTime" class="text-danger">
                                                <span v-for="error in errors.BeginWorkTime">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.IncidentWitness }">Incident Witness: <i v-if="!incident.IncidentWitness" class="fa fa-asterisk text-danger small"></i></label>
                                        <div class="col-sm-8">

                                            <input type="text" v-model="incident.IncidentWitness" :class="{ 'form-control': true, 'is-invalid': errors.IncidentWitness }" placeholder="Enter Incident Witness" />

                                            <span v-if="errors.IncidentWitness" class="text-danger">
                                                <span v-for="error in errors.IncidentWitness">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.CrewSize }">
                                            Crew Size: <i class="fa fa-asterisk text-danger small" v-if="settings.inci_require_crew_size == 1 && !incident.CrewSize"></i>
                                        </label>
                                        <div class="col-sm-8">

                                            <b-form-input type="number"
                                                v-model="incident.CrewSize"
                                                :class="{'is-invalid': errors.CrewSize }"
                                                placeholder="Enter Crew Size"
                                                style="max-width: 100px;"
                                                pattern="[0-9]*" inputmode="numeric" />

                                            <span v-if="errors.CrewSize" class="text-danger">
                                                <span v-for="error in errors.CrewSize">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.due_to_rework }">Did Incident Occur Due to Rework?</label>
                                        <div class="col-sm-8">
                                            <b-form-checkbox v-model="incident.due_to_rework" :value="1" :unchecked-value="0" plain>YES</b-form-checkbox>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-4 col-form-label text-sm-right': true, 'text-danger': errors.MajorBodyPartId }">
                                            Major Body Part: <i class="fa fa-asterisk text-danger small" v-if="settings.inci_require_major_body_part == 1 && !incident.MajorBodyPartId"></i>
                                        </label>
                                        <div class="col-lg-8 col-md-8">
                                            <b-form-select v-model="incident.MajorBodyPartId" :options="majorBodyPartOptions" :value="null" :class="{'is-invalid': errors.MajorBodyPartId}" />

                                            <span v-if="errors.MajorBodyPartId" class="text-danger">
                                                <span v-for="error in errors.MajorBodyPartId">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-4 col-form-label text-sm-right': true, 'text-danger': errors.SpecificBodyPartId }">
                                            Specific Body Part: <i class="fa fa-asterisk text-danger small" v-if="settings.inci_require_specific_body_part == 1 && !incident.SpecificBodyPartId"></i>
                                        </label>
                                        <div class="col-lg-8 col-md-8">
                                            <b-form-select v-model="incident.SpecificBodyPartId" :options="specificBodyPartOptions" :value="null" :class="{'is-invalid': errors.SpecificBodyPartId}" />

                                            <span v-if="errors.SpecificBodyPartId" class="text-danger">
                                                <span v-for="error in errors.SpecificBodyPartId">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-4 col-form-label text-sm-right': true, 'text-danger': errors.InjuryTypeId }">
                                            Nature of Injury: <i class="fa fa-asterisk text-danger small" v-if="settings.inci_require_injury_nature == 1 && !incident.InjuryTypeId"></i>
                                        </label>
                                        <div class="col-lg-8 col-md-8">
                                            <b-form-select v-model="incident.InjuryTypeId" :options="injuryTypeOptions" :value="null" :class="{'is-invalid': errors.InjuryTypeId}" />

                                            <span v-if="errors.InjuryTypeId" class="text-danger">
                                                <span v-for="error in errors.InjuryTypeId">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>


                                    <template v-if="incidentType.sections">
                                        <incident-sections-builder
                                            :sections="incidentTypeSections"
                                            :initialValues="custom_fields"
                                            :errors="errors"
                                            baseSection="incident"
                                            labelSize="col-lg-3 col-md-3 col-sm-4"
                                            fieldSize="col-sm-8"
                                            @customFieldsChanged="setIncidentCustomFields" ></incident-sections-builder>
                                    </template>
                                    <hr/>

                                    <div class="form-group row">
                                        <label :class="{ 'col-md-4 col-form-label text-sm-right': true, 'text-danger': errors.IsPtpCompleted }">
                                            Was a PTP Completed for the Work Being Performed?
                                        </label>
                                        <div class="col-md-5">
                                            <b-form-select v-model="incident.IsPtpCompleted" :options="ptpAnswers" :state="errors.IsPtpCompleted ? false : null" />

                                            <span v-if="errors.IsPtpCompleted" class="text-danger">
                                                <span v-for="error in errors.IsPtpCompleted">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-md-4 col-form-label text-sm-right': true, 'text-danger': errors.IsPtpThorough }">
                                            Was the PTP Thorough and Identified All Hazards and Corrective Measures for the Task?
                                        </label>
                                        <div class="col-md-5">
                                            <b-form-select v-model="incident.IsPtpThorough" :options="ptpAnswers" :state="errors.IsPtpThorough ? false : null" />

                                            <span v-if="errors.IsPtpThorough" class="text-danger">
                                                <span v-for="error in errors.IsPtpThorough">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="alert alert-warning" v-if="incident.IsPtpCompleted == 1 && settings.inci_ptp_reminder">
                                        <strong>REMINDER:</strong> Make sure to attach a copy of the PTP Report to the Incident.
                                    </div>
                                </div>
                            </div>

                            <div class="card mb-3" id="incident-crew">
                                <div :class="{ 'card-header': true, 'text-danger': errors.CrewMembers }">
                                    <i class="fa fa-group"></i> <strong>Crew Members</strong> <i class="fa fa-asterisk text-danger small" v-if="settings.inci_require_crew_members == 1 && !incident.CrewMembers"></i>

                                    <button class="btn btn-sm btn-light btn-toggle" v-on:click="baseSections.crew_members = !baseSections.crew_members" :title="(baseSections.crew_members ? 'Hide' : 'Show') + ' Crew Members Section'">
                                        <i :class="'fa fa-caret-' + (baseSections.crew_members ? 'up' : 'down')"></i>
                                    </button>
                                </div>
                                <div :class="{'card-body': true, 'd-none': !baseSections.crew_members}">
                                    <vue-editor v-model="incident.CrewMembers" placeholder="Enter Crew Members" :editor-toolbar="this.$root.customVueEditorToolbar"></vue-editor>
                                    <div v-if="errors.CrewMembers" class="text-danger">
                                        <span v-for="error in errors.CrewMembers">{{ error }}</span>
                                    </div>

                                    <div v-if="incidentType.sections" class="mt-4">
                                        <incident-sections-builder
                                            :sections="incidentTypeSections"
                                            :initialValues="custom_fields"
                                            :errors="errors"
                                            baseSection="crew_members"
                                            labelSize="col-lg-3 col-md-3 col-sm-4"
                                            fieldSize="col-sm-8"
                                            @customFieldsChanged="setIncidentCustomFields"></incident-sections-builder>
                                    </div>
                                </div>
                            </div>

                            <div class="card mb-3" id="incident-participants">
                                <div class="card-header">
                                    <i class="fa fa-group"></i> <strong>Investigation Participants</strong>
                                    <button class="btn btn-sm btn-light btn-toggle" v-on:click="baseSections.participants = !baseSections.participants" :title="(baseSections.participants ? 'Hide' : 'Show') + ' Investigation Participants Section'">
                                        <i :class="'fa fa-caret-' + (baseSections.participants ? 'up' : 'down')"></i>
                                    </button>
                                </div>
                                <div :class="{'card-body': true, 'd-none': !baseSections.participants}">
                                    <vue-editor v-model="incident.incident_participants" placeholder="Enter Investigation Participants" :editor-toolbar="this.$root.customVueEditorToolbar"></vue-editor>

                                    <div v-if="incidentType.sections" class="mt-4">
                                        <incident-sections-builder
                                            :sections="incidentTypeSections"
                                            :initialValues="custom_fields"
                                            :errors="errors"
                                            baseSection="participants"
                                            labelSize="col-lg-3 col-md-3 col-sm-4"
                                            fieldSize="col-sm-8"
                                            @customFieldsChanged="setIncidentCustomFields" ></incident-sections-builder>
                                    </div>
                                </div>
                            </div>

                            <div class="card mb-3" id="incident-work">
                                <div :class="{ 'card-header': true, 'text-danger': errors.TypeOfWorkPerformed }">
                                    <strong>Type of Work Being Performed at Time of Incident</strong>
                                    <i v-if="!incident.TypeOfWorkPerforme && settings.inci_require_type_of_work == 1" class="fa fa-asterisk text-danger small"></i>

                                    <button class="btn btn-sm btn-light btn-toggle" v-on:click="baseSections.type_of_work = !baseSections.type_of_work" :title="(baseSections.type_of_work ? 'Hide' : 'Show') + ' Type of Work Section'">
                                        <i :class="'fa fa-caret-' + (baseSections.type_of_work ? 'up' : 'down')"></i>
                                    </button>
                                </div>
                                <div :class="{'card-body': true, 'd-none': !baseSections.type_of_work}">
                                    <vue-editor v-model="incident.TypeOfWorkPerformed" placeholder="Enter Type of Work Being Performed at Time of Incident" :editor-toolbar="this.$root.customVueEditorToolbar"></vue-editor>
                                    <div v-if="errors.TypeOfWorkPerformed" class="text-danger">
                                        <span v-for="error in errors.TypeOfWorkPerformed">{{ error }}</span>
                                    </div>

                                    <div v-if="incidentType.sections" class="mt-4">
                                        <incident-sections-builder
                                            :sections="incidentTypeSections"
                                            :initialValues="custom_fields"
                                            :errors="errors"
                                            baseSection="type_of_work"
                                            labelSize="col-lg-3 col-md-3 col-sm-4"
                                            fieldSize="col-sm-8"
                                            @customFieldsChanged="setIncidentCustomFields" ></incident-sections-builder>
                                    </div>
                                </div>
                            </div>

                            <div class="card mb-3" id="incident-description">
                                <div :class="{ 'card-header': true, 'text-danger': errors.DetailedDescription }">
                                    <strong>Detailed Description of Incident </strong> <i v-if="!incident.DetailedDescription" class="fa fa-asterisk text-danger small"></i>
                                    <button class="btn btn-sm btn-light btn-toggle" v-on:click="baseSections.description = !baseSections.description" :title="(baseSections.description ? 'Hide' : 'Show') + ' Description Section'">
                                        <i :class="'fa fa-caret-' + (baseSections.description ? 'up' : 'down')"></i>
                                    </button>
                                </div>
                                <div :class="{'card-body': true, 'd-none': !baseSections.description}">
                                    <vue-editor v-model="incident.DetailedDescription" placeholder="Enter a Detailed Description of the Incident" :editor-toolbar="this.$root.customVueEditorToolbar"></vue-editor>
                                    <div v-if="errors.DetailedDescription" class="text-danger">
                                        <span v-for="error in errors.DetailedDescription">{{ error }}</span>
                                    </div>

                                    <div v-if="incidentType.sections" class="mt-4">
                                        <incident-sections-builder
                                            :sections="incidentTypeSections"
                                            :initialValues="custom_fields"
                                            :errors="errors"
                                            baseSection="description"
                                            labelSize="col-lg-3 col-md-3 col-sm-4"
                                            fieldSize="col-sm-8"
                                            @customFieldsChanged="setIncidentCustomFields" ></incident-sections-builder>
                                    </div>
                                </div>
                            </div>

                            <div class="card mb-3" id="rca-hazards">
                                <div class="card-header">
                                    <strong :class="{'text-danger': errors.UnsafeActOrPractice || errors.UnsafeCondition}"><i class="fa fa-bar-chart"></i> Root Cause Analysis</strong>
                                    <i v-if="(!incident.UnsafeActOrPractice || !incident.UnsafeCondition)  && settings.inci_require_root_cause == 1" class="fa fa-asterisk text-danger small"></i>

                                    <button class="btn btn-sm btn-light btn-toggle" v-on:click="baseSections.root_cause = !baseSections.root_cause" :title="(baseSections.root_cause ? 'Hide' : 'Show') + ' Root Cause Analysis Section'">
                                        <i :class="'fa fa-caret-' + (baseSections.root_cause ? 'up' : 'down')"></i>
                                    </button>
                                </div>
                                <div :class="{'card-body': true, 'd-none': !baseSections.root_cause}">
                                    <div class="form-group row">
                                        <label class="col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right">
                                            Hazards:

                                            <div class="mt-1" v-if="$store.state.user.is_admin">
                                                <a href="javascript:void(0)" v-on:click="showNewHazardModal()"><small><i class="fa fa-plus"></i> Create New Hazard</small></a>
                                            </div>

                                            <div>
                                                <hazard-modal ref="newHazardModal" @hazardCreated="hazardCreated" :showButton="false" />
                                            </div>
                                        </label>
                                        <div class="col-sm-8">
                                            <multiselect v-model="incident.hazards" placeholder="Search Hazards" label="hazard_name" track-by="hazard_id" :options="hazards" :multiple="true" :taggable="true"></multiselect>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label class="col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right">
                                            Contributing Behaviors:

                                            <div class="mt-1" v-if="$store.state.user.is_admin">
                                                <a href="javascript:void(0)" v-on:click="showNewBehaviorModal()"><small><i class="fa fa-plus"></i> Create New Behavior</small></a>
                                            </div>

                                            <div>
                                                <behavior-modal ref="newBehaviorModal" @behaviorCreated="behaviorCreated" :showButton="false" />
                                            </div>
                                        </label>
                                        <div class="col-sm-8">
                                            <multiselect v-model="incident.behaviors" placeholder="Search Behaviors" label="behavior_name" track-by="behavior_id" :options="behaviors" :multiple="true" :taggable="true"></multiselect>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label class="col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right">
                                            Contributing Conditions:

                                            <div class="mt-1" v-if="$store.state.user.is_admin">
                                                <a href="javascript:void(0)" v-on:click="showNewConditionModal()"><small><i class="fa fa-plus"></i> Create New Condition</small></a>
                                            </div>

                                            <div>
                                                <condition-modal ref="newConditionModal" @conditionCreated="conditionCreated" :showButton="false" />
                                            </div>
                                        </label>
                                        <div class="col-sm-8">
                                            <multiselect v-model="incident.conditions" placeholder="Search Conditions" label="condition_name" track-by="condition_id" :options="conditions" :multiple="true" :taggable="true"></multiselect>
                                        </div>
                                    </div>
                                    <template v-if="incidentType.sections">
                                        <incident-sections-builder
                                            :sections="incidentTypeSections"
                                            :initialValues="custom_fields"
                                            :errors="errors"
                                            baseSection="root_cause"
                                            labelSize="col-lg-3 col-md-3 col-sm-4"
                                            fieldSize="col-sm-8"
                                            @customFieldsChanged="setIncidentCustomFields" ></incident-sections-builder>
                                    </template>
                                    <hr/>

                                    <label id="rca-acts" :class="{'mt-3': true, 'text-danger': errors.UnsafeActOrPractice}">
                                        <strong>Unsafe Act(s)/Work Practice Contributing to Incident</strong>
                                        <i v-if="!incident.UnsafeActOrPractice && settings.inci_require_root_cause == 1" class="fa fa-asterisk text-danger small"></i>
                                    </label>

                                    <vue-editor v-model="incident.UnsafeActOrPractice" placeholder="Enter Any Unsafe Acts or Work Practices that lead to the incident" :editor-toolbar="this.$root.customVueEditorToolbar"></vue-editor>

                                    <span v-if="errors.UnsafeActOrPractice" class="text-danger">
                                        <span v-for="error in errors.UnsafeActOrPractice">{{ error }}</span>
                                    </span>
                                    <hr/>

                                    <label id="rca-conditions" :class="{'mt-3': true, 'text-danger': errors.UnsafeCondition}">
                                        <strong>Unsafe Condition (If Applicable)</strong>
                                        <i v-if="!incident.UnsafeCondition && settings.inci_require_root_cause == 1" class="fa fa-asterisk text-danger small"></i>
                                    </label>

                                    <vue-editor v-model="incident.UnsafeCondition" placeholder="Enter Unsafe Condition(s)" :editor-toolbar="this.$root.customVueEditorToolbar"></vue-editor>
                                    <span v-if="errors.UnsafeCondition" class="text-danger">
                                        <span v-for="error in errors.UnsafeCondition">{{ error }}</span>
                                    </span>
                                </div>
                            </div>

                            <div class="card mb-3" id="corrective-actions">
                                <div class="card-header" :class="{'text-danger': errors.actions}">
                                    <strong>Corrective Actions </strong>
                                    <i v-if="(!incident.actions || incident.actions.length === 0) && settings.inci_require_correct_action == 1" class="fa fa-asterisk text-danger small"></i>

                                    <button class="btn btn-sm btn-light btn-toggle" v-on:click="baseSections.actions = !baseSections.actions" :title="(baseSections.actions ? 'Hide' : 'Show') + ' Corrective Actions Section'">
                                        <i :class="'fa fa-caret-' + (baseSections.actions ? 'up' : 'down')"></i>
                                    </button>
                                    <b-button size="sm" variant="info" title="Add a Corrective Action" v-on:click="newAction"><i class="fa fa-plus-circle"></i> Corrective Action</b-button>
                                </div>
                                <div :class="{'card-body': true, 'd-none': !baseSections.actions}">
                                    <div v-if="errors.actions" class="alert alert-danger">
                                        <ul>
                                            <li v-for="error in errors.actions">{{ error }}</li>
                                        </ul>
                                    </div>
                                    <corrective-action-table-list :actions="actions" view="cards" :hideActions="true" @editAction="handleEditAction" @deleteAction="handleDeleteAction" :showModalEdit="true"></corrective-action-table-list>
                                    <br/>

                                    <template v-if="incidentType.sections">
                                        <incident-sections-builder
                                            :sections="incidentTypeSections"
                                            :initialValues="custom_fields"
                                            :errors="errors"
                                            baseSection="actions"
                                            labelSize="col-lg-3 col-md-3 col-sm-4"
                                            fieldSize="col-sm-8"
                                            @customFieldsChanged="setIncidentCustomFields" ></incident-sections-builder>
                                    </template>
                                </div>
                            </div>

                            <div class="card mb-3" id="injury-input">
                                <div class="card-header">
                                    <strong><i class="fa fa-heartbeat"></i> Injury/Medical</strong>
                                    <button class="btn btn-sm btn-light btn-toggle" v-on:click="baseSections.injury = !baseSections.injury" :title="(baseSections.injury ? 'Hide' : 'Show') + ' Injury/Medical Section'">
                                        <i :class="'fa fa-caret-' + (baseSections.injury ? 'up' : 'down')"></i>
                                    </button>
                                </div>
                                <div :class="{'card-body': true, 'd-none': !baseSections.injury}">
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.InjuryObjectOrSubstance }">What Object or Substance Directly Harmed Employee?:</label>
                                        <div class="col-sm-8">

                                            <input type="text" v-model="incident.InjuryObjectOrSubstance" :class="{ 'form-control': true, 'is-invalid': errors.InjuryObjectOrSubstance }" placeholder="Enter the Injury Object" />

                                            <span v-if="errors.InjuryObjectOrSubstance" class="text-danger">
                                                <span v-for="error in errors.InjuryObjectOrSubstance">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.MedicalProfessional }">Medical Professional:</label>
                                        <div class="col-sm-8">

                                            <input type="text" v-model="incident.MedicalProfessional" :class="{ 'form-control': true, 'is-invalid': errors.MedicalProfessional }" placeholder="Enter Medical Professional" />

                                            <span v-if="errors.MedicalProfessional" class="text-danger">
                                                <span v-for="error in errors.MedicalProfessional">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.IsReturnToWork }">Did Employee Return to Work?</label>
                                        <div class="col-sm-8">
                                            <select v-model="incident.IsReturnToWork" class="form-control" style="max-width: 150px;">
                                                <option :value="null">-- Returned to Work? --</option>
                                                <option :value="1">Yes</option>
                                                <option :value="0">No</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.ReturnToWorkDate }">Date Returned to Work:</label>
                                        <div class="col-sm-8">

                                            <date-picker :value="readDate(incident.ReturnToWorkDate)" @input="incident.ReturnToWorkDate = writeDate($event)" :config="{format: 'MM/DD/YYYY', useCurrent: false}" placeholder="Enter Date Returned to Work"></date-picker>

                                            <span v-if="errors.ReturnToWorkDate" class="text-danger">
                                                <span v-for="error in errors.ReturnToWorkDate">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <hr/>

                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.FacilityTypeId }">Place Of Treatment:</label>
                                        <div class="col-sm-8">

                                            <b-form-select v-model="incident.FacilityTypeId" :options="facilityTypeOptions" />

                                            <span v-if="errors.FacilityTypeId" class="text-danger">
                                                <span v-for="error in errors.FacilityTypeId">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.FacilityName }">Facility Name:</label>
                                        <div class="col-sm-8">

                                            <input type="text" v-model="incident.FacilityName" :class="{ 'form-control': true, 'is-invalid': errors.FacilityName }" placeholder="Enter Treament Facility Name" />

                                            <span v-if="errors.FacilityName" class="text-danger">
                                                <span v-for="error in errors.FacilityName">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.FacilityAddress }">Facility Address:</label>
                                        <div class="col-sm-8">

                                            <input type="text" v-model="incident.FacilityAddress" :class="{ 'form-control': true, 'is-invalid': errors.FacilityAddress }" placeholder="Enter Treament Facility Address" />

                                            <span v-if="errors.FacilityAddress" class="text-danger">
                                                <span v-for="error in errors.FacilityAddress">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.FacilityCity }">Facility City:</label>
                                        <div class="col-sm-8">

                                            <input type="text" v-model="incident.FacilityCity" :class="{ 'form-control': true, 'is-invalid': errors.FacilityCity }" placeholder="Enter Treament Facility City" />

                                            <span v-if="errors.FacilityCity" class="text-danger">
                                                <span v-for="error in errors.FacilityCity">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.FacilityState }">Facility State (Code):</label>
                                        <div class="col-sm-8">

                                            <input type="text" v-model="incident.FacilityState" :class="{ 'form-control': true, 'is-invalid': errors.FacilityState }" placeholder="Ex) NY" style="max-width: 100px;" />

                                            <span v-if="errors.FacilityState" class="text-danger">
                                                <span v-for="error in errors.FacilityState">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.FacilityZip }">Facility Zip:</label>
                                        <div class="col-sm-8">

                                            <input type="text" v-model="incident.FacilityZip" :class="{ 'form-control': true, 'is-invalid': errors.FacilityZip }" placeholder="Zip Code" style="max-width: 100px" />

                                            <span v-if="errors.FacilityZip" class="text-danger">
                                                <span v-for="error in errors.FacilityZip">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>

                                    <template v-if="incidentType.sections">
                                        <incident-sections-builder
                                            :sections="incidentTypeSections"
                                            :initialValues="custom_fields"
                                            :errors="errors"
                                            baseSection="injury"
                                            labelSize="col-lg-3 col-md-3 col-sm-4"
                                            fieldSize="col-sm-8"
                                            @customFieldsChanged="setIncidentCustomFields" ></incident-sections-builder>
                                    </template>
                                </div>
                            </div>

                            <div class="card mb-3" id="additional-input">
                                <div class="card-header">
                                    <strong>Additional Information</strong>
                                    <button class="btn btn-sm btn-light btn-toggle" v-on:click="baseSections.additional = !baseSections.additional" :title="(baseSections.additional ? 'Hide' : 'Show') + ' Additional Information Section'">
                                        <i :class="'fa fa-caret-' + (baseSections.additional ? 'up' : 'down')"></i>
                                    </button>
                                </div>
                                <div :class="{'card-body': true, 'd-none': !baseSections.additional}">
                                    <div class="form-group row">
                                        <label class="col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right">Reporter:</label>
                                        <div class="col-sm-8">

                                            <template v-if="incident.IncidentId">
                                                {{ incident.UserFirstName }} {{ incident.UserLastName }} <span v-if="incident.UserTitle">| {{ incident.UserTitle }}</span>
                                            </template>
                                            <template v-else>
                                                {{ this.$store.state.user.first_name }} {{ this.$store.state.user.last_name }} <span v-if="this.$store.state.user.title">| {{ this.$store.state.user.title }}</span>
                                            </template>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label class="col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right">Report Created:</label>
                                        <div class="col-sm-8">

                                            <template v-if="incident.IncidentId">
                                                {{ incident.created_at | moment("MM/DD/YYYY") }}
                                            </template>
                                            <template v-else>
                                                {{ (new Date()) | moment("MM/DD/YYYY")  }}
                                            </template>
                                        </div>
                                    </div>

                                    <template v-if="incidentType.sections">
                                        <incident-sections-builder
                                            :sections="incidentTypeSections"
                                            :initialValues="custom_fields"
                                            :errors="errors"
                                            baseSection="additional"
                                            labelSize="col-lg-3 col-md-3 col-sm-4"
                                            fieldSize="col-sm-8"
                                            @customFieldsChanged="setIncidentCustomFields" ></incident-sections-builder>
                                    </template>
                                    <hr/>

                                    <div class="form-group">
                                        <label :class="{ 'col-form-label text-sm-right': true, 'text-danger': errors.AdditionalInformation }">Please List Any Additional or Relevant Information:</label>
                                        <div class="input-group-sm">

                                            <vue-editor v-model="incident.AdditionalInformation" placeholder="Enter Any Additional or Relevant Information" :editor-toolbar="this.$root.customVueEditorToolbar"></vue-editor>

                                            <span v-if="errors.AdditionalInformation" class="text-danger">
                                                <span v-for="error in errors.AdditionalInformation">{{ error }}</span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="card mb-3" id="admin-input" v-if="this.$store.state.user.is_admin">
                                <div class="card-header">
                                    <strong>Admin Information</strong>
                                    <button class="btn btn-sm btn-light btn-toggle" v-on:click="baseSections.admin = !baseSections.admin" :title="(baseSections.admin ? 'Hide' : 'Show') + ' Admin Section'">
                                        <i :class="'fa fa-caret-' + (baseSections.admin ? 'up' : 'down')"></i>
                                    </button>
                                </div>
                                <div :class="{'card-body': true, 'd-none': !baseSections.admin}">
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-4 col-form-label text-sm-right': true, 'text-danger': errors.IsOshaRecordable }">OSHA Recordable</label>
                                        <div class="col-lg-8 col-md-8">
                                            <b-form-checkbox v-model="incident.IsOshaRecordable" :value="1" :unchecked-value="0" plain>YES</b-form-checkbox>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-4 col-form-label text-sm-right': true, 'text-danger': errors.OshaCaseNumber }">OSHA Case #</label>
                                        <div class="col-lg-8 col-md-8">
                                            <b-form-input v-model="incident.OshaCaseNumber" type="text" placeholder="Enter Osha Case Number"></b-form-input>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-4 col-form-label text-sm-right': true, 'text-danger': errors.OshaInjuryTypeId }">OSHA Injury Typ:</label>
                                        <div class="col-lg-8 col-md-8">
                                            <b-form-select v-model="incident.OshaInjuryTypeId" :options="oshaInjuryTypeOptions" :value="null" />
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-4 col-form-label text-sm-right': true, 'text-danger': errors.OshaOutcomeTypeId }">OSHA Most Serious Outcom:</label>
                                        <div class="col-lg-8 col-md-8">
                                            <b-form-select v-model="incident.OshaOutcomeTypeId" :options="oshaOutcomeTypeOptions" :value="null" />
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-4 col-form-label text-sm-right': true, 'text-danger': errors.date_first_away_from_work }">Date First Away from Work:</label>
                                        <div class="col-lg-8 col-md-8">
                                            <b-form-datepicker
                                                v-model="incident.date_first_away_from_work"
                                                today-button
                                                reset-button
                                                close-button
                                                placeholder="Date Employee was first Away from Work" />
                                            <a href="javascript:void(0)" v-on:click="setDate('date_first_away_from_work')">Set to Today</a>
                                            &nbsp;|&nbsp;<a href="javascript:void(0)" class="text-danger" v-on:click="incident.date_first_away_from_work = null">Clear Date</a>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-4 col-form-label text-sm-right': true, 'text-danger': errors.date_returned_away_from_work }">Date Returned to Work:</label>
                                        <div class="col-lg-8 col-md-8">
                                            <b-form-datepicker
                                                v-model="incident.date_returned_away_from_work"
                                                today-button
                                                reset-button
                                                close-button
                                                placeholder="Date Employee Returned to Work" />

                                            <a href="javascript:void(0)" v-on:click="setDate('date_returned_away_from_work')">Set to Today</a>
                                            &nbsp;|&nbsp;<a href="javascript:void(0)" class="text-danger" v-on:click="incident.date_returned_away_from_work = null">Clear Date</a>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-4 col-form-label text-sm-right': true, 'text-danger': errors.AwayFromWorkDays }">Days Away from Work:</label>
                                        <div class="col-lg-8 col-md-8">
                                            <b-form-input v-model="incident.AwayFromWorkDays" type="text" placeholder="Number of Days Aways from Work"></b-form-input>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-4 col-form-label text-sm-right': true, 'text-danger': errors.date_first_transfer_or_restrict }">Date First Transferred/Restricted:</label>
                                        <div class="col-lg-8 col-md-8">
                                            <b-form-datepicker
                                                v-model="incident.date_first_transfer_or_restrict"
                                                today-button
                                                reset-button
                                                close-button
                                                placeholder="Date Employee was first Transferred or Restricted" />

                                            <a href="javascript:void(0)" v-on:click="setDate('date_first_transfer_or_restrict')">Set to Today</a>
                                            &nbsp;|&nbsp;<a href="javascript:void(0)" class="text-danger" v-on:click="incident.date_first_transfer_or_restrict = null">Clear Date</a>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-4 col-form-label text-sm-right': true, 'text-danger': errors.date_returned_transfer_or_restrict }">Date Returned from Transfer/Restriciton:</label>
                                        <div class="col-lg-8 col-md-8">
                                            <b-form-datepicker
                                                v-model="incident.date_returned_transfer_or_restrict"
                                                today-button
                                                reset-button
                                                close-button
                                                placeholder="Date Employee Returned from Transfer or Restriction" />

                                            <a href="javascript:void(0)" v-on:click="setDate('date_returned_transfer_or_restrict')">Set to Today</a>
                                            &nbsp;|&nbsp;<a href="javascript:void(0)" class="text-danger" v-on:click="incident.date_returned_transfer_or_restrict = null">Clear Date</a>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-4 col-form-label text-sm-right': true, 'text-danger': errors.JobTransferOrRestrictionDays }">Days on Job Transfer or Restriction:</label>
                                        <div class="col-lg-8 col-md-8">
                                            <b-form-input v-model="incident.JobTransferOrRestrictionDays" type="text" placeholder="Job Transfer or Restrict Days"></b-form-input>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-4 col-form-label text-sm-right': true, 'text-danger': errors.DeathDate }">Date of Death:</label>
                                        <div class="col-lg-8 col-md-8">
                                            <date-picker :value="readDate(incident.DeathDate)" @input="incident.DeathDate = writeDate($event)" :config="{format: 'MM/DD/YYYY', useCurrent: false}" placeholder="Enter Death Date if Applicable"></date-picker>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label :class="{ 'col-lg-3 col-md-4 col-form-label text-sm-right': true, 'text-danger': errors.IsDotRecordable }">DOT Reportable</label>
                                        <div class="col-lg-8 col-md-8">
                                            <b-form-checkbox v-model="incident.IsDotRecordable" :value="1" :unchecked-value="0" plain>YES</b-form-checkbox>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="card mb-3" id="files-input">
                                <div class="card-header">
                                    <strong><i class="fa fa-files-o"></i> Files &amp; Photos</strong>
                                    <button class="btn btn-sm btn-light btn-toggle" v-on:click="baseSections.files = !baseSections.files" :title="(baseSections.files ? 'Hide' : 'Show') + ' Files Section'">
                                        <i :class="'fa fa-caret-' + (baseSections.files ? 'up' : 'down')"></i>
                                    </button>
                                </div>
                                <div :class="{'card-body': true, 'd-none': !baseSections.files}">
                                    <template v-if="incident.IncidentId">
                                        <file-uploader assetType="Incident" :assetId="incident.IncidentId" />
                                        <hr/>

                                        <file-list :files="files" />
                                    </template>
                                    <template v-else>
                                        <div class="alert alert-info">
                                            In order to better process file uploads, you must first save the incident.
                                        </div>
                                    </template>

                                    <template v-if="incidentType.sections">
                                        <incident-sections-builder
                                            :sections="incidentTypeSections"
                                            :initialValues="custom_fields"
                                            :errors="errors"
                                            baseSection="files"
                                            labelSize="col-lg-3 col-md-3 col-sm-4"
                                            fieldSize="col-sm-8"
                                            @customFieldsChanged="setIncidentCustomFields" ></incident-sections-builder>
                                    </template>
                                </div>
                            </div>

                            <template v-if="incidentType.sections">
                                <incident-sections-builder :sections="incidentTypeSections" :initialValues="custom_fields" @customFieldsChanged="setIncidentCustomFields" :errors="errors"></incident-sections-builder>
                            </template>

                            <corrective-action-modal ref="correctiveActionModal" @actionSaved="updateActions" :ajaxMode="false" :enableFileUploads="false" />

                        </div>
                    </div>
                    <div class="col-xl-3 d-xl-block d-none">

                    </div>
                </div>

                <button type="button" class="btn btn-success" v-on:click="saveIncident(this)" :disabled="saving">
                    <span v-if="saving">Saving...</span>
                    <span v-else-if="IncidentId">Update</span>
                    <span v-else>Create</span>
                    Incident
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import moment from 'moment-timezone';

    export default {
        data() {
            return {
                loading: true,
                saving: false,
                edit_lock: false,
                IncidentId: this.$route.params.id,
                userTimezone: this.$store.state.user.timezone,
                incident: {},
                custom_fields: {},
                incidentType: {},
                files: [],
                hazards: {},
                behaviors: {},
                conditions: {},
                incidentTypes: {},
                facilityTypes: {},
                actions: [],
                injuryTypes: {},
                majorBodyParts: {},
                specificBodyParts: {},
                oshaInjuryTypes: {},
                oshaOutcomeTypes: {},
                action: {},
                disc_action: {},
                ptpAnswers: [
                    { value: '', text: '-- Select a Value --' },
                    { value: 1, text: 'Yes' },
                    { value: 0, text: 'No' },
                    { value: null, text: 'N/A' },
                ],
                errors: {},
                autoSaveId: null,
                account: {},
                settings: {},
                incidentTypeSettings: {},
                baseSections: {
                    all: false,
                    admin: false,
                    files: false,
                    additional: false,
                    injury: false,
                    actions: false,
                    description: false,
                    type_of_work: false,
                    participants: false,
                    disc_actions: false,
                    crew_members: false,
                    incident: false,
                    equipment: false,
                    root_cause: false,
                    employee: false,
                    jobsite: false,
                },
                is_other_location: 0,
                is_other_supervisor: 0,
                no_jobsite_set: 0,
            }
        },

        computed: {
            //Major Body Part Options
            injuryTypeOptions(){
                //Setup options
                var options = [
                    {value: null, text: '-- Select the Nature of Injury --'},
                ];

                //Loop through and build lists
                for(var i = 0; i < this.injuryTypes.length; i++)
                {
                    //Push onto options
                    options.push({value: this.injuryTypes[i].InjuryTypeId, text: this.injuryTypes[i].TypeName});
                }

                //Send it
                return options;
            },

            //Major Body Part Options
            majorBodyPartOptions(){
                //Setup options
                var options = [
                    {value: null, text: '-- Select a Major Body Part --'},
                ];

                //Loop through and build lists
                for(var i = 0; i < this.majorBodyParts.length; i++)
                {
                    //Push onto options
                    options.push({value: this.majorBodyParts[i].MajorBodyPartId, text: this.majorBodyParts[i].BodyPartName});
                }

                //Send it
                return options;
            },

            //Major Body Part Options
            specificBodyPartOptions(){
                //Setup options
                var options = [
                    {value: null, text: '-- Select a Specific Body Part --'},
                ];

                //Loop through and build lists
                for(var i = 0; i < this.specificBodyParts.length; i++)
                {
                    //Push onto options
                    options.push({value: this.specificBodyParts[i].SpecificBodyPartId, text: this.specificBodyParts[i].BodyPartName});
                }

                //Send it
                return options;
            },

            oshaInjuryTypeOptions(){
                //Setup options
                var options = [
                    {value: null, text: '-- Select an OSHA Injury Type --'},
                ];

                //Loop through and build lists
                for(var i = 0; i < this.oshaInjuryTypes.length; i++)
                {
                    //Push onto options
                    options.push({value: this.oshaInjuryTypes[i].OshaInjuryTypeId, text: this.oshaInjuryTypes[i].TypeName});
                }

                //Send it
                return options;
            },

            oshaOutcomeTypeOptions(){
                //Setup options
                var options = [
                    {value: null, text: '-- Select an OSHA Outcome Type --'},
                ];

                //Loop through and build lists
                for(var i = 0; i < this.oshaOutcomeTypes.length; i++)
                {
                    //Push onto options
                    options.push({value: this.oshaOutcomeTypes[i].OshaOutcomeTypeId, text: this.oshaOutcomeTypes[i].TypeName});
                }

                //Send it
                return options;
            },

            facilityTypeOptions(){
                //Setup options
                var options = [
                    {value: null, text: '-- Select a Facility Type --'},
                ];

                //Loop through and build lists
                for(var i = 0; i < this.facilityTypes.length; i++)
                {
                    //Push onto options
                    options.push({value: this.facilityTypes[i].FacilityTypeId, text: this.facilityTypes[i].TypeName});
                }

                //Send it
                return options;
            },

            //Major Body Part Options
            subcontractorOptions(){
                //Setup options
                var options = [
                    {value: null, text: '-- Select Subcontractor --'},
                ];

                //Loop through and build lists
                for(var i = 0; i < this.subcontractors.length; i++)
                {
                    //Push onto options
                    options.push({value: this.subcontractors[i].subcontractor_id, text: this.subcontractors[i].subcontractor_name});
                }

                //Send it
                return options;
            },

            JobsiteValues(){
                //check for null
                if(!this.incident.JobsiteId)
                {
                    return {};
                }

                return {JobsiteId: this.incident.JobsiteId, JobsiteNumber: this.incident.JobsiteNumber, JobsiteName: this.incident.JobsiteName};
            },

            ForemanValues(){
                //check for null
                if(!this.incident.foreman_id)
                {
                    return null;
                }

                //Return the values
                return {EmployeeId: this.incident.foreman_id, FirstName: this.incident.ForemanFirstName, LastName: this.incident.ForemanLastName, EmployeeNumber: this.incident.ForemanEmployeeNumber};
            },

            LeadmanValues(){
                //check for null
                if(!this.incident.leadman_id)
                {
                    return null;
                }

                return {EmployeeId: this.incident.leadman_id, FirstName: this.incident.LeadmanFirstName, LastName: this.incident.LeadmanLastName, EmployeeNumber: this.incident.LeadmanEmployeeNumber};
            },

            EmployeeValues(){
                //check for null
                if(!this.incident.EmployeeId)
                {
                    return {};
                }

                return {EmployeeId: this.incident.EmployeeId, FirstName: this.incident.EmpFirstName, LastName: this.incident.EmpLastName, EmployeeNumber: this.incident.EmployeeEmployeeNumber};
            },

            SuperintendentValues(){
                //check for null
                if(!this.incident.superintendent_id)
                {
                    return {};
                }

                return {
                    EmployeeId: this.incident.superintendent_id,
                    FirstName: this.incident.SuperintendentFirstName,
                    LastName: this.incident.SuperintendentLastName,
                    email: this.incident.SuperintendentEmail,
                    EmployeeNumber: this.incident.SuperintendentEmployeeNumber
                };
            },

            ProjectManagerValues(){
                //check for null
                if(!this.incident.project_manager_id)
                {
                    return {};
                }

                return {
                    EmployeeId: this.incident.project_manager_id,
                    FirstName: this.incident.ProjectManagerFirstName,
                    LastName: this.incident.ProjectManagerLastName,
                    email: this.incident.ProjectManagerEmail,
                    EmployeeNumber: this.incident.ProjectManagerEmployeeNumber
                };
            },

            SupervisorValues(){
                //check for null
                if(!this.incident.supervisor_id)
                {
                    return {};
                }

                return {
                    EmployeeId: this.incident.supervisor_id,
                    FirstName: this.incident.SupervisorFirstName,
                    LastName: this.incident.SupervisorLastName,
                    email: this.incident.SupervisorEmail,
                    EmployeeNumber: this.incident.SupervisorEmployeeNumber,
                };
            },

            EquipmentValues(){
                //Check for Null
                if(!this.incident.equipment_id)
                {
                    return null;
                }

                //Send it
                return {
                    equipment_id: this.incident.equipment_id,
                    model_number: this.incident.model_number,
                    serial_number: this.incident.serial_number,
                    equipment_name: this.incident.equipment_name,
                    equipment_type_name: this.incident.equipment_type_name
                };
            },

            incidentTypeSections(){
                //Check it
                if(!this.incidentType.sections)
                {
                    return [];
                }

                //Parse
                return this.incidentType.sections;
            },

            customFieldFileTypes(){
                //Loop and build
                var fields = [];

                //Loop through Sections
                for (var i = this.incidentTypeSections.length - 1; i >= 0; i--)
                {
                    //Loop through the fields
                    for (var j = this.incidentTypeSections[i].fields.length - 1; j >= 0; j--)
                    {
                        //Check the type
                        if(this.incidentTypeSections[i].fields[j].type == 'File')
                        {
                            fields.push(this.incidentTypeSections[i].fields[j]);
                        }
                    }
                }

                //Send it
                return fields;
            },

            showIncidentHeaderAsterisk() {
                if(!this.incident.IncidentTypeId
                    || !this.incident.IncidentDate
                    || !this.incident.IncidentTime
                    || !this.incident.IncidentLocation
                    || !this.incident.IncidentReportedDate
                    || !this.incident.BeginWorkTime
                    || !this.incident.IncidentWitness
                    || (this.settings.inci_require_crew_size == 1 && !this.incident.CrewSize))
                {
                    return true;
                }

                return false;
            }
        },

        watch: {
            is_other_location(newValue, oldValue){
                // Don't run this watcher if we're trying to nuke all location data
                if (this.no_jobsite_set == 1)
                {
                    return;
                }

                //Make sure it's instantiated
                if(!this.$refs.inciJobsiteSelect)
                {
                    return;
                }

                //Set the Jobsite to NULL
                if(newValue == 1)
                {
                    this.incident.JobsiteId = null;
                    this.$refs.inciJobsiteSelect.clearInput();
                }
                else
                {
                    this.$refs.inciJobsiteSelect.setValue(this.JobsiteValues);
                    this.incident.other_location = null;
                }
            },

            is_other_supervisor(newValue, oldValue){
                //Check the value
                if(newValue == 1)
                {
                    this.incident.supervisor_id = null;
                    this.$refs.incSupervisorSelect.clearInput();
                }
                else
                {
                    //Clear the other
                    this.incident.other_supervisor_name = null;
                }
            },

            'incident.is_other_subcontractor'(newVal, oldVal){
                //Check for 1
                if(newVal == 1)
                {
                    //Clear the value
                    this.incident.subcontractor_id = null;
                }
                else
                {
                    //Clear the plain text value
                    this.incident.ContractorCompany = null;
                }
            },

            'incident.disc_action_required'(newValue){
                //If value = 1, show modal
                if(newValue == 1 && !this.incident.disc_action_id)
                {
                    this.$refs.discEditModal.show();
                }
            },

            no_jobsite_set(newValue, oldValue){
                //Set the Jobsite to NULL
                if(newValue == 1)
                {
                    this.$refs.inciJobsiteSelect.clearInput();
                    this.is_other_location = 0;
                    this.incident.other_location = null;
                }
                else
                {
                    this.$refs.inciJobsiteSelect.setValue(this.JobsiteValues);
                }
            },

            'incident.date_first_away_from_work'(newVal, oldVal){
                //Call the helper
                this.setDaysAway();
            },

            'incident.date_returned_away_from_work'(newVal, oldVal){
                //Call the helper
                this.setDaysAway();
            },

            'incident.date_first_transfer_or_restrict'(newVal, oldVal){
                //Call the helper
                this.setDaysTransfer();
            },

            'incident.date_returned_transfer_or_restrict'(newVal, oldVal){
                //Call the helper
                this.setDaysTransfer();
            },
        },

        methods: {
            // Convert database date to human
            readDate(date) {
                return moment(date, 'YYYY-MM-DD').format('MM/DD/YYYY');
            },

            // Convert human date to database
            writeDate(date) {
                return moment(date, 'MM/DD/YYYY').format('YYYY-MM-DD');
            },

            setDate(attribute, date = null){
                //setup
                if(date == null)
                {
                    //Set it
                    date = (new moment()).format('YYYY-MM-DD');
                }
                //Set it
                this.incident[attribute] = date;
            },

            //Get the Various Resources We Need to Edit a Incident
            getErrorClass: function(attribute) {
                return {
                    'form-group has-error': this.errors[attribute],
                    'form-group': !this.errors[attribute],
                }
            },

            newAction(){
                // Set the id for the modal
                this.$refs.correctiveActionModal.setAction({temp_action_id: (new Date()).getTime()});

                //Activate the modal
                this.$refs.correctiveActionModal.showModal();
            },

            handleEditAction(action){
                // Set the id for the modal
                this.$refs.correctiveActionModal.setAction(action);

                //Activate the modal
                this.$refs.correctiveActionModal.showModal();
            },

            handleDeleteAction(action){
                //Check for confirm
                if(!confirm('Are you sure you want to delete this action?'))
                {
                    return;
                }

                //Check for a temp id
                if(action.temp_action_id && !action.corrective_action_id)
                {
                    //Delete it
                    this.updateActions(action, true);

                    //Stop here
                    return;
                }

                //Send it
                this.$http
                    .post('/corrective-actions/delete/' + action.corrective_action_id)
                    .then(response => {
                        //Alert
                        this.$toastr.success('Corrective Action Deleted');

                        //Delete the bid
                        this.updateActions(action, true);
                    });
            },

            updateActions(action, deleteMode){
                //We need to check to see if the bid is new, or updated
                for (var i = this.actions.length - 1; i >= 0; i--)
                {
                    //If the IDs match, replace it
                    if((action.corrective_action_id && this.actions[i].corrective_action_id == action.corrective_action_id) ||
                        (action.temp_action_id && this.actions[i].temp_action_id == action.temp_action_id))
                    {
                        //Check if we are deleting it
                        if(typeof deleteMode != 'undefined' && deleteMode == true)
                        {
                            this.actions.splice(i, 1);
                            return;
                        }

                        //Set the value
                        this.actions.splice(i, 1, action);
                        //this.$refs.correctiveActionModal.hideModal();

                        //Stop here;
                        return;
                    }
                }

                //If it gets here, it's a new bid. Push it
                this.actions.push(action);
                //this.$refs.correctiveActionModal.hideModal();
            },

            onJobSiteSelectUpdate (jobsite) {
              //Check for null
              if(jobsite == null)
              {
                  //Stop
                  this.incident.JobsiteId = null;
                  return;
              }

              //Send the value
              this.incident.JobsiteId = jobsite.JobsiteId;
              //this.incident.JobsiteName = jobsite.JobsiteName;
              //this.incident.JobsiteNumber = jobsite.JobsiteNumber;
            },

            onForemanSelect(employee){
                if (employee == null)
                {
                    this.incident.foreman_id = null;
                    return;
                }

                this.incident.foreman_id = employee.EmployeeId;
            },

            onLeadmanSelect(employee){
                if (employee == null)
                {
                    this.incident.leadman_id = null;
                    return;
                }

                this.incident.leadman_id = employee.EmployeeId;
            },

            onSuperSelect(employee){
                if (employee == null)
                {
                    this.incident.superintendent_id = null;
                    return;
                }

                this.incident.superintendent_id = employee.EmployeeId;
            },

            superCreated(employee){
                this.incident.superintendent_id = employee.EmployeeId;
                this.incident.SuperintendentFirstName = employee.FirstName;
                this.incident.SuperintendentLastName = employee.LastName;
                this.incident.SuperintendentEmail = employee.email;
                this.incident.SuperintendentEmployeeNumber = employee.EmployeeNumber;

                //We have to call the employee-select helper function
                //because component props are ONE WAY databound.
                this.$refs.incSuperSelect.setSelectedValues(this.SuperintendentValues);
            },

            showNewSuperModal(){
                //Show it
                this.$refs.newSuperModal.show();
            },

            onProjectManagerSelect(employee){
                if (employee == null)
                {
                    this.incident.project_manager_id = null;
                    return;
                }

                this.incident.project_manager_id = employee.EmployeeId;
            },

            projectManagerCreated(employee){
                this.incident.project_manager_id = employee.EmployeeId;
                this.incident.ProjectManagerFirstName = employee.FirstName;
                this.incident.ProjectManagerLastName = employee.LastName;
                this.incident.ProjectManagerEmail = employee.email;
                this.incident.ProjectManagerEmployeeNumber = employee.EmployeeNumber;

                //We have to call the employee-select helper function
                //because component props are ONE WAY databound.
                this.$refs.incProjectManagerSelect.setSelectedValues(this.ProjectManagerValues);
            },

            showNewProjectManagerModal(){
                //Show it
                this.$refs.newProjectManagerModal.show();
            },

            onSupervisorSelect(employee){
                if (employee == null)
                {
                    this.incident.supervisor_id = null;
                    return;
                }

                this.incident.supervisor_id = employee.EmployeeId;
            },

            onEquipmentSelect(equipment){
                if (equipment == null)
                {
                    this.incident.equipment_id = null;
                    return;
                }

                this.incident.equipment_id = equipment.equipment_id;
            },

            onEmployeeSelect(employee){
                if (employee == null)
                {
                    this.incident.EmployeeId = null;
                    this.$set(this.incident, 'EmployeeFirstName', null);
                    this.$set(this.incident, 'EmployeeLastName', null);
                    this.$set(this.incident, 'EmployeeAddress', null);
                    this.$set(this.incident, 'EmployeeCity', null);
                    this.$set(this.incident, 'EmployeeState', null);
                    this.$set(this.incident, 'EmployeeZip', null);
                    this.$set(this.incident, 'EmployeePhone', null);
                    this.$set(this.incident, 'EmployeeTitle', null);

                    return;
                }

                //Set the ID
                this.incident.EmployeeId = employee.EmployeeId;

                //Reactivity Helper
                this.$set(this.incident, 'EmployeeFirstName', employee.FirstName);
                this.$set(this.incident, 'EmployeeLastName', employee.LastName);
                this.$set(this.incident, 'EmployeeAddress', employee.Address);
                this.$set(this.incident, 'EmployeeCity', employee.City);
                this.$set(this.incident, 'EmployeeState', employee.State);
                this.$set(this.incident, 'EmployeeZip', employee.Zip);
                this.$set(this.incident, 'EmployeePhone', employee.Phone);

                //Check for a TitleName
                if (employee.TitleName)
                {
                    this.$set(this.incident, 'EmployeeTitle', employee.TitleName);
                }
                else
                {
                    this.$set(this.incident, 'EmployeeTitle', employee.position);
                }
            },

            addEmployeeSelected(employee){
                //Check for null
                if(!employee)
                {
                    return;
                }

                //Make sure that this employee hasn't already been added
                for(var i = 0; i < this.incident.additional_employees.length; i++)
                {
                    //Check all the IDs
                    if(this.incident.additional_employees[i].EmployeeId == employee.EmployeeId)
                    {
                        //Reset
                        this.$refs.addEmployeeSelector.clearInput(null);

                        //Stop here
                        return;
                    }
                }

                //Push it
                this.incident.additional_employees.push(employee);

                //Reset
                this.$refs.addEmployeeSelector.clearInput(null);
            },

            removeAddEmployee(employeeId)
            {
                //Make sure that this employee hasn't already been added
                for(var i = 0; i < this.incident.additional_employees.length; i++)
                {
                    //Check all the IDs
                    if(this.incident.additional_employees[i].EmployeeId == employeeId)
                    {
                        //Splice it
                        this.incident.additional_employees.splice(i, 1);

                        //Stop here
                        return;
                    }
                }
            },

            addEquipmentSelected(equipment){
                //Check for null
                if(!equipment)
                {
                    return;
                }

                //Make sure that this equipment hasn't already been added
                for(var i = 0; i < this.incident.additional_equipment.length; i++)
                {
                    //Check all the IDs
                    if(this.incident.additional_equipment[i].equipment_id == equipment.equipment_id)
                    {
                        //Reset
                        this.$refs.addEquipmentSelector.clearInput(null);

                        //Stop here
                        return;
                    }
                }

                //Push it
                this.incident.additional_equipment.push(equipment);

                //Reset
                this.$refs.addEquipmentSelector.clearInput(null);
            },

            removeAddEquipment(equipmentId)
            {
                //Make sure that this employee hasn't already been added
                for(var i = 0; i < this.incident.additional_equipment.length; i++)
                {
                    //Check all the IDs
                    if(this.incident.additional_equipment[i].equipment_id == equipmentId)
                    {
                        //Splice it
                        this.incident.additional_equipment.splice(i, 1);

                        //Stop here
                        return;
                    }
                }
            },

            foremanCreated(employee){
                //How do we show the
                this.incident.foreman_id = employee.EmployeeId;
                this.incident.ForemanFirstName = employee.FirstName;
                this.incident.ForemanLastName = employee.LastName;
                this.incident.ForemanEmployeeNumber = employee.EmployeeNumber;

                //We have to call the employee-select helper function
                //because component props are ONE WAY databound.
                this.$refs.foremanSelector.setSelectedValues(this.ForemanValues);
            },

            leadmanCreated(employee){
                //How do we show the
                this.incident.leadman_id = employee.EmployeeId;
                this.incident.LeadmanFirstName = employee.FirstName;
                this.incident.LeadmanLastName = employee.LastName;
                this.incident.LeadmanEmployeeNumber = employee.EmployeeNumber;

                //We have to call the employee-select helper function
                //because component props are ONE WAY databound.
                this.$refs.leadmanSelector.setSelectedValues(this.LeadmanValues);
            },

            showNewEmployeeModal(){
                //Show it
                this.$refs.newEmployeeModal.show();
            },

            employeeCreated(employee){
                //How do we show the
                this.incident.EmployeeId = employee.EmployeeId;
                this.incident.EmpFirstName = employee.FirstName;
                this.incident.EmpLastName = employee.LastName;
                this.incident.EmployeeEmployeeNumber = employee.EmployeeNumber;

                //We have to call the employee-select helper function
                //because component props are ONE WAY databound.
                this.$refs.employeeSelector.setSelectedValues(this.EmployeeValues);
            },

            subcontractorCreated(subcontractor){
                //Do What?
                this.subcontractors.push(subcontractor);

                //Set the ID
                this.incident.subcontractor_id = subcontractor.subcontractor_id;
            },

            getIncident() {
                //Get the data
                this.$http
                    .get('incident/edit' + (this.IncidentId ? '/' + this.IncidentId : ''))
                    .then(response => {
                        //Set all the data
                        this.files = response.data.files;
                        this.incidentTypes = response.data.incidentTypes;
                        this.incidentTypes[''] = '--Select an Incident Type --';
                        this.facilityTypes = response.data.facilityTypes;
                        this.hazards = response.data.hazards;
                        this.behaviors = response.data.behaviors;
                        this.conditions = response.data.conditions;
                        this.actions = response.data.actions;
                        this.injuryTypes = response.data.injuryTypes;
                        this.majorBodyParts = response.data.majorBodyParts;
                        this.specificBodyParts = response.data.specificBodyParts;
                        this.oshaInjuryTypes = response.data.oshaInjuryTypes;
                        this.oshaOutcomeTypes = response.data.oshaOutcomeTypes;
                        this.incident = response.data.incident;
                        this.account = response.data.account;
                        this.settings = response.data.settings;
                        this.incidentType = response.data.incidentType;
                        this.incidentTypeSettings = response.data.incidentTypeSettings;
                        this.disc_action = response.data.disc_action;
                        this.subcontractors = response.data.subcontractors;

                        //First Check Edit Lock
                        if(response.data.edit_lock && response.data.edit_lock == true)
                        {
                            //Set it
                            this.edit_lock = true;
                        }

                        //Check for empty
                        //if(!Array.isArray(response.data.custom_fields))
                        if(typeof response.data.custom_fields === 'object')
                        {
                            //Set it
                            this.custom_fields = response.data.custom_fields;
                        }

                        //Set some helpers
                        if(this.incident.other_location)
                        {
                            this.is_other_location = 1;
                        }

                        //Set Other Super
                        if(this.incident.other_supervisor_name)
                        {
                            this.is_other_supervisor = 1;
                        }

                        if (!this.incident.JobsiteId && !this.incident.other_location)
                        {
                            this.no_jobsite_set = 1;
                        }
                    })
                    .catch(response => {

                    })
                    .then(() => {
                        //Set loading
                        this.loading = false;
                    });
            },

            saveIncident: function() {
                //SHow loading
                this.saving = true;

                //Reset the errors
                this.errors = {};

                //Build the incident
                this.buildIncidentInputs();

                //Build the custom field Files
                var customFieldFiles = {};

                //Loop and Build
                for (var i = this.customFieldFileTypes.length - 1; i >= 0; i--)
                {
                    //Push onto the fields
                    if(this.custom_fields[this.customFieldFileTypes[i].id])
                    {
                        //Set the field type
                        customFieldFiles['custom_field_file_'+this.customFieldFileTypes[i].id] = this.custom_fields[this.customFieldFileTypes[i].id];

                        //Delete from the incident
                        //delete this.incident.custom_fields[this.customFieldFileTypes[i].id];
                    }
                }

                //Send it
                this.$http
                .postWithFiles('incident/edit' + (this.IncidentId ? '/' + this.IncidentId : ''), this.incident, customFieldFiles)
                .then(response => {
                    //Alert the user?
                    this.$toastr.success('Incident Saved');

                    //Return to main list?
                    this.$router.push('/incidents/view/' + response.data.IncidentId);
                })
                .catch(error => {
                    //Check for error
                    if(error.response.status === 422)
                    {
                        //Display validation errors
                        this.errors = error.response.data
                    }
                    else
                    {
                        //Alert the user
                        this.$toastr.error(error.response.data, 'Failed to Save Incident');
                    }
                })
                .then(response => {
                    //SHow loading
                    this.saving = false;
                });
            },

            buildIncidentInputs(){
                //Build the hazard ids
                this.incident.hazard_ids = [];
                this.incident.behavior_ids = [];
                this.incident.condition_ids = [];
                this.incident.equipment_ids = [];
                this.incident.actions = this.actions;
                this.incident.is_other_location = this.is_other_location;
                this.incident.is_other_supervisor = this.is_other_supervisor;
                this.incident.no_jobsite_set = this.no_jobsite_set;

                //Loop
                for(var i = 0; i < (this.incident.hazards ? this.incident.hazards.length : 0); i++)
                {
                    this.incident.hazard_ids.push(this.incident.hazards[i]['hazard_id']);
                }

                //Loop
                for(var i = 0; i < (this.incident.behaviors ? this.incident.behaviors.length : 0); i++)
                {
                    this.incident.behavior_ids.push(this.incident.behaviors[i]['behavior_id']);
                }

                //Loop
                for(var i = 0; i < (this.incident.conditions ? this.incident.conditions.length : 0); i++)
                {
                    this.incident.condition_ids.push(this.incident.conditions[i]['condition_id']);
                }

                //Loop
                for(var i = 0; i < (this.incident.additional_equipment ? this.incident.additional_equipment.length : 0); i++)
                {
                    this.incident.equipment_ids.push(this.incident.additional_equipment[i]['equipment_id']);
                }

                //Build All the Section Field Values
                if(this.incidentTypeSections.length > 0)
                {
                    //Loop and build
                    for (var i = this.incidentType.length - 1; i >= 0; i--) {
                        //Get the values
                        this.incidentType[i]
                    }
                }

                //Set the custom fields
                this.incident.custom_fields = this.custom_fields;
            },

            autoSave(){
                //Build the incident
                this.buildIncidentInputs();

                //Send it
                this.$http
                .post('incident/auto-save/' + this.IncidentId, this.incident)
                .then(response => {
                    //Do nothing
                })
                .catch(error => {
                    //Check for error
                    if(error.response.status === 422)
                    {
                        //Display validation errors
                        //this.errors = error.response.data
                    }
                    else
                    {

                    }

                    //Clear the interval on any error
                    this.clearAutoSave();
                })
            },

            setAutoSave(){
                //Space
                const self = this;

                //Set it
                this.autoSaveId = setInterval(function(){
                    //Fire Auto save
                    self.autoSave();
                }, 30000);
            },

            clearAutoSave(){
                //Clear it
                clearInterval(this.autoSaveId);

                //Set it
                this.autoSaveId = null;
            },

            toggleAllSections(){
                //If TRUE, set all to false
                var keys = Object.keys(this.baseSections);

                //Setter
                var setValue = !this.baseSections.all;

                //Loop
                for (var i = keys.length - 1; i >= 0; i--)
                {
                    this.baseSections[keys[i]] = setValue;
                }

                //Done;
                return;
            },

            setIncidentCustomFields(fields){
                //Set it
                this.custom_fields = fields;
            },

            setIncidentDiscAction(action){
                //Set the action
                this.incident.disc_action_id = action.disc_action_id;

                //Set the action?
                this.disc_action = action;
            },

            showDiscAction(){
                //Show the modal
                this.$refs.discEditModal.show();
            },

            showNewHazardModal(){
                //Show the model
                this.$refs.newHazardModal.show();
            },

            hazardCreated(hazard){
                //Push onto the hazards
                this.hazards.push(hazard);
                this.incident.hazards.push(hazard);
            },

            showNewBehaviorModal(){
                //Show the model
                this.$refs.newBehaviorModal.show();
            },

            behaviorCreated(behavior){
                //Push onto the behaviors
                this.behaviors.push(behavior);
                this.incident.behaviors.push(behavior);
            },

            showNewConditionModal(){
                //Show the model
                this.$refs.newConditionModal.show();
            },

            conditionCreated(condition){
                //Push onto the conditions
                this.conditions.push(condition);
                this.incident.conditions.push(condition);
            },

            setDaysAway(){
                //Make sure we are not loading it
                if(this.loading)
                {
                    return;
                }

                //Check both dates
                if(!this.incident.date_first_away_from_work || !this.incident.date_returned_away_from_work)
                {
                    return;
                }

                //Calculate the number of business days away from work
                var start = new moment(this.incident.date_first_away_from_work);
                var end = new moment(this.incident.date_returned_away_from_work);

                //SEnd it
                this.incident.AwayFromWorkDays = end.diff(start, 'days');
            },

            setDaysTransfer(){
                //Make sure we are not loading it
                if(this.loading)
                {
                    return;
                }

                //Check both dates
                if(!this.incident.date_first_transfer_or_restrict || !this.incident.date_returned_transfer_or_restrict)
                {
                    return;
                }

                //Calculate the number of business days away from work
                var start = new moment(this.incident.date_first_transfer_or_restrict);
                var end = new moment(this.incident.date_returned_transfer_or_restrict);

                //SEnd it
                this.incident.JobTransferOrRestrictionDays = end.diff(start, 'days');
            }
        },

        mounted() {
            //Get the incidents
            this.getIncident();

            //Set the auto-save
            this.setAutoSave();
        },

        beforeDestroy(){
            //Clear it
            this.clearAutoSave();
        }
    }
</script>
